import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useAuthMediaUrl } from "@/lib/authMedia";

// MediaZoomViewer — a full-screen, deep-zoom look at one storyboard image or
// clip. Owner report 2026-10-05: the board's thumbnails could only be enlarged
// a little and details (a second pair of sunglasses, a hand) were still too
// small to judge. Here every picture zooms up to MAX_SCALE: wheel or trackpad,
// pinch on a touch screen, double-tap/double-click, the +/− buttons or the big
// slider. Drag pans while zoomed; ←/→ (or the arrows) step through the board
// without leaving the viewer. Drawn on the page (a portal) so the canvas's own
// pan/zoom transform never scales or clips it.

export type ZoomItem = { src: string | null | undefined; kind?: "image" | "video"; label?: string };

export const MAX_SCALE = 16;
const MIN_SCALE = 1;
const DOUBLE_TAP_MS = 320;
const TAP_SLOP_PX = 8;

type View = { s: number; x: number; y: number };
const IDENTITY: View = { s: 1, x: 0, y: 0 };
const clampScale = (s: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

/** Keep the zoomed content covering the stage: no panning off into empty space. */
function clampView(v: View, w: number, h: number): View {
  const s = clampScale(v.s);
  return {
    s,
    x: Math.min(0, Math.max(w - w * s, v.x)),
    y: Math.min(0, Math.max(h - h * s, v.y)),
  };
}

/** Scale to `s` while the stage point (px, py) stays under the finger/cursor. */
function zoomAt(v: View, s: number, px: number, py: number): View {
  const next = clampScale(s);
  const k = next / v.s;
  return { s: next, x: px - (px - v.x) * k, y: py - (py - v.y) * k };
}

export default function MediaZoomViewer({
  items, index, onIndex, onClose,
}: {
  items: ZoomItem[];
  index: number;
  onIndex: (index: number) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const item = items[index] ?? null;
  const url = useAuthMediaUrl(item?.src || "");
  const isVideo = item?.kind === "video";
  const stageRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [view, setView] = useState<View>(IDENTITY);
  // Animate button/double-tap/slider jumps; follow fingers and wheel 1:1.
  const [animate, setAnimate] = useState(false);
  const [paused, setPaused] = useState(false);
  const viewRef = useRef(view);
  viewRef.current = view;

  const stageSize = () => {
    const el = stageRef.current;
    return { w: el?.clientWidth || 1, h: el?.clientHeight || 1 };
  };
  const apply = useCallback((next: View, smooth: boolean) => {
    const { w, h } = stageSize();
    setAnimate(smooth);
    setView(clampView(next, w, h));
  }, []);
  const zoomBy = useCallback((factor: number, smooth = true) => {
    const { w, h } = stageSize();
    apply(zoomAt(viewRef.current, viewRef.current.s * factor, w / 2, h / 2), smooth);
  }, [apply]);
  const setScale = useCallback((s: number, smooth: boolean) => {
    const { w, h } = stageSize();
    apply(zoomAt(viewRef.current, s, w / 2, h / 2), smooth);
  }, [apply]);

  // A new picture starts whole.
  useEffect(() => { setAnimate(false); setView(IDENTITY); setPaused(false); }, [index]);

  const go = useCallback((dir: -1 | 1) => {
    const next = index + dir;
    if (next >= 0 && next < items.length) onIndex(next);
  }, [index, items.length, onIndex]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
      else if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
      else if (e.key === "+" || e.key === "=") { e.preventDefault(); zoomBy(1.5); }
      else if (e.key === "-" || e.key === "_") { e.preventDefault(); zoomBy(1 / 1.5); }
      else if (e.key === "0") { e.preventDefault(); apply(IDENTITY, true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose, zoomBy, apply]);

  // Wheel / trackpad: a native, non-passive listener so the page never scrolls
  // underneath. exp() keeps small trackpad deltas as smooth as wheel notches.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      const factor = Math.exp(-dy * (e.ctrlKey ? 0.01 : 0.0018));
      const v = viewRef.current;
      setAnimate(false);
      setView(clampView(zoomAt(v, v.s * factor, e.clientX - rect.left, e.clientY - rect.top), el.clientWidth, el.clientHeight));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  // Pointers: one drags (pan), two pinch. Works for mouse, pen and touch.
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<{ startView: View; startDist: number; startMid: { x: number; y: number }; startPt: { x: number; y: number }; moved: boolean } | null>(null);
  const lastTap = useRef<{ at: number; x: number; y: number } | null>(null);

  const local = (e: React.PointerEvent) => {
    const rect = stageRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const beginGesture = () => {
    const pts = [...pointers.current.values()];
    const [a, b] = pts;
    gesture.current = {
      startView: viewRef.current,
      startDist: b ? Math.hypot(a.x - b.x, a.y - b.y) : 0,
      startMid: b ? { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } : a,
      startPt: a,
      moved: gesture.current?.moved || pts.length > 1,
    };
  };
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* jsdom */ }
    pointers.current.set(e.pointerId, local(e));
    if (pointers.current.size === 1) gesture.current = null;
    beginGesture();
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(e.pointerId) || !gesture.current) return;
    pointers.current.set(e.pointerId, local(e));
    const g = gesture.current;
    const pts = [...pointers.current.values()];
    const { w, h } = stageSize();
    let next: View;
    if (pts.length >= 2 && g.startDist > 0) {
      const [a, b] = pts;
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const s = clampScale(g.startView.s * (dist / g.startDist));
      const k = s / g.startView.s;
      next = { s, x: mid.x - (g.startMid.x - g.startView.x) * k, y: mid.y - (g.startMid.y - g.startView.y) * k };
      g.moved = true;
    } else {
      const p = pts[0];
      const dx = p.x - g.startPt.x;
      const dy = p.y - g.startPt.y;
      if (Math.hypot(dx, dy) > TAP_SLOP_PX) g.moved = true;
      next = { s: g.startView.s, x: g.startView.x + dx, y: g.startView.y + dy };
    }
    setAnimate(false);
    setView(clampView(next, w, h));
  };
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(e.pointerId)) return;
    const pt = local(e);
    pointers.current.delete(e.pointerId);
    const wasTap = !gesture.current?.moved && pointers.current.size === 0;
    if (pointers.current.size > 0) { beginGesture(); return; }
    gesture.current = null;
    if (!wasTap || e.type === "pointercancel") return;
    const now = Date.now();
    const prev = lastTap.current;
    if (prev && now - prev.at < DOUBLE_TAP_MS && Math.hypot(prev.x - pt.x, prev.y - pt.y) < 30) {
      lastTap.current = null;
      const v = viewRef.current;
      apply(v.s > 1.05 ? IDENTITY : zoomAt(v, 3, pt.x, pt.y), true);
    } else {
      lastTap.current = { at: now, x: pt.x, y: pt.y };
    }
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (paused) v.pause(); else void v.play()?.catch?.(() => { /* autoplay refused; the button still works */ });
  }, [paused, url]);

  if (!item || typeof document === "undefined") return null;

  const btn: React.CSSProperties = {
    alignItems: "center", background: "rgba(255,255,255,.08)", border: "1px solid #2a3648", borderRadius: 12,
    color: "#e2e8f0", cursor: "pointer", display: "inline-flex", fontSize: 20, fontWeight: 700,
    height: 48, justifyContent: "center", minWidth: 48, padding: "0 12px", touchAction: "manipulation",
  };
  const pct = Math.round(view.s * 100);
  // The slider is logarithmic so 1×→2× gets as much travel as 8×→16×.
  const sliderVal = Math.log(view.s) / Math.log(MAX_SCALE);

  return createPortal(
    <div
      role="dialog" aria-modal="true" aria-label={t("studio.zoom.title")}
      data-testid="media-zoom-viewer"
      style={{ background: "#03050a", color: "#e2e8f0", display: "flex", flexDirection: "column", inset: 0, position: "fixed", zIndex: 350 }}
    >
      <div style={{ alignItems: "center", display: "flex", gap: 8, padding: "10px 12px", borderBottom: "1px solid #1c2636", flexShrink: 0 }}>
        <div style={{ flex: 1, fontSize: 14, fontWeight: 700, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {item.label || t("studio.zoom.itemN", { n: index + 1 })}
          <span style={{ color: "#7c8aa0", fontWeight: 400 }}> · {index + 1}/{items.length}</span>
        </div>
        <button type="button" style={btn} onClick={onClose} aria-label={t("studio.zoom.close")} data-testid="media-zoom-close">✕</button>
      </div>

      <div
        ref={stageRef}
        data-testid="media-zoom-stage"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{ cursor: view.s > 1 ? "grab" : "zoom-in", flex: 1, minHeight: 0, overflow: "hidden", position: "relative", touchAction: "none", userSelect: "none" }}
      >
        <div
          data-testid="media-zoom-content"
          data-scale={view.s.toFixed(2)}
          style={{
            height: "100%", left: 0, position: "absolute", top: 0, transformOrigin: "0 0", width: "100%",
            transform: `translate3d(${view.x}px, ${view.y}px, 0) scale(${view.s})`,
            transition: animate ? "transform .22s cubic-bezier(.2,.8,.2,1)" : "none",
            willChange: "transform",
          }}
        >
          {!url ? (
            <div style={{ alignItems: "center", color: "#7c8aa0", display: "flex", fontSize: 13, height: "100%", justifyContent: "center" }}>{t("studio.zoom.loading")}</div>
          ) : isVideo ? (
            <video ref={videoRef} key={url} src={url} autoPlay muted loop playsInline draggable={false}
              style={{ display: "block", height: "100%", objectFit: "contain", pointerEvents: "none", width: "100%" }} />
          ) : (
            <img src={url} alt={item.label || ""} draggable={false}
              style={{ display: "block", height: "100%", imageRendering: view.s > 4 ? "pixelated" : "auto", objectFit: "contain", pointerEvents: "none", width: "100%" }} />
          )}
        </div>
        {items.length > 1 && (
          <>
            <button type="button" aria-label={t("studio.zoom.previous")} disabled={index <= 0}
              onPointerDown={(e) => e.stopPropagation()} onClick={() => go(-1)}
              style={{ ...btn, left: 10, opacity: index <= 0 ? 0.3 : 1, position: "absolute", top: "50%", transform: "translateY(-50%)", background: "rgba(10,14,21,.8)", fontSize: 30 }}>‹</button>
            <button type="button" aria-label={t("studio.zoom.next")} disabled={index >= items.length - 1}
              onPointerDown={(e) => e.stopPropagation()} onClick={() => go(1)}
              style={{ ...btn, opacity: index >= items.length - 1 ? 0.3 : 1, position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "rgba(10,14,21,.8)", fontSize: 30 }}>›</button>
          </>
        )}
      </div>

      <div style={{ alignItems: "center", borderTop: "1px solid #1c2636", display: "flex", flexShrink: 0, flexWrap: "wrap", gap: 10, justifyContent: "center", padding: "10px 12px" }}>
        <button type="button" style={btn} onClick={() => zoomBy(1 / 1.5)} aria-label={t("studio.zoom.zoomOut")} data-testid="media-zoom-out">−</button>
        <input
          type="range" min={0} max={1} step={0.001} value={sliderVal}
          onChange={(e) => setScale(Math.pow(MAX_SCALE, Number(e.target.value)), false)}
          aria-label={t("studio.zoom.zoomLevel")} data-testid="media-zoom-slider"
          className="studio-range" style={{ flex: "1 1 220px", maxWidth: 420 }}
        />
        <button type="button" style={btn} onClick={() => zoomBy(1.5)} aria-label={t("studio.zoom.zoomIn")} data-testid="media-zoom-in">+</button>
        <button type="button" style={{ ...btn, fontSize: 14, fontVariantNumeric: "tabular-nums", minWidth: 76 }} onClick={() => apply(IDENTITY, true)}
          title={t("studio.zoom.reset")} data-testid="media-zoom-readout">{pct}%</button>
        {isVideo && (
          <button type="button" style={{ ...btn, fontSize: 14 }} onClick={() => setPaused((p) => !p)}>
            {paused ? t("studio.zoom.play") : t("studio.zoom.pause")}
          </button>
        )}
        <div style={{ color: "#7c8aa0", flexBasis: "100%", fontSize: 11, textAlign: "center" }}>{t("studio.zoom.hint")}</div>
      </div>
    </div>,
    document.body,
  );
}
